import { create } from 'zustand';
import type { AIEraserScene } from '@/domains/tools/types';

export type ManualEraserType = 'smear' | 'rect';

/** 批量模式自动消除类型：与 AIEraserScene 对齐，最多开启一项 */
export type BatchAutoEraserType = AIEraserScene | null;

export type BatchImageStatus = 'normal' | 'processing' | 'success' | 'failed' | 'unrecognized' | 'downloadable';

export interface BatchImageItem {
  id: string;
  status: BatchImageStatus;
  url: string;
  width?: number;
  height?: number;
  resultUrl?: string;
  /** 已拿到 resultUrl 但结果图仍在加载中，用于保持 loading 遮罩直到图片 onLoad */
  resultImageLoading?: boolean;
  progress?: number;
  errorText?: string;
  selected?: boolean;
  selectable?: boolean;
  /** 服务端任务 id（msgid），创建任务后由 ids 映射绑定 */
  taskId?: string;
}

interface AIEraserStore {
  singleOriginalImage: string;
  singleResultImage: string;
  /** 单张模式最近一次生成结果对应的服务端任务 id。 */
  singleResultTaskId: string;
  singleCompareIng: boolean;
  /** 单张打开的媒体类型：image 或 video */
  singleMediaType: 'image' | 'video';
  mode: 'single' | 'batch';
  manualEnableEraser: boolean;
  brushSize: number;
  singleEraserLoading: boolean;
  textSingleAutoEraser: boolean;
  waterMarkSingleAutoEraser: boolean;
  glassesSingleAutoEraser: boolean;
  strangerSingleAutoEraser: boolean;
  reflectionSingleAutoEraser: boolean;
  batchImages: BatchImageItem[];
  batchMaxCount: number;
  batchProcessingMode: boolean;
  previewImageIndex: number | null;
  /** 批量模式自动消除：最多开启一项，点击消除时读取此值 */
  batchAutoEraserType: BatchAutoEraserType;
  /** 批量消除进行中 */
  batchEraserLoading: boolean;
  /** 单张模式「重新上传」触发打开文件选择，与批量分离避免两个 ImageUploader 同 key 重复弹窗 */
  singleReuploadSignal: number;
  /** 批量模式「重新上传」触发打开文件选择 */
  batchReuploadSignal: number;
  /** 批量模式：点击重新上传后为 true，下次选择图片确认上传后替换列表并置回 false */
  batchReuploadPending: boolean;
  // 单图mask列表
  singleMaskImages: Record<'people' | 'glasses' | 'watermark', string>;
}

const useAIEraserStore = create<AIEraserStore>(() => ({
  // 单图模式原始图
  singleOriginalImage: '',
  // 单图模式结果图
  singleResultImage: '',
  singleResultTaskId: '',
  // 单图模式对比中
  singleCompareIng: false,
  singleMediaType: 'image',
  // 模式：单图 | 批量
  mode: 'single',
  // 单图模式消除loading
  singleEraserLoading: false,
  // 手动消除是否启用橡皮擦
  manualEnableEraser: false,
  // 涂抹笔画粗细
  brushSize: 20,
  // 单张-自动消除文字
  textSingleAutoEraser: false,
  // 单张-自动消除水印
  waterMarkSingleAutoEraser: false,
  // 单张-自动消除眼镜
  glassesSingleAutoEraser: false,
  // 单张-自动消除路人
  strangerSingleAutoEraser: false,
  // 单张-自动消除反光
  reflectionSingleAutoEraser: false,
  // 批量图片列表（默认为空，每项必须携带 url）
  batchImages: [],
  // 批量处理最大数量
  batchMaxCount: 30,
  // 是否处于批量选择处理状态
  batchProcessingMode: false,
  // 当前大图预览的图片索引（null 为列表模式）
  previewImageIndex: null,
  /** 批量自动消除类型，null 表示未选 */
  batchAutoEraserType: null,
  /** 批量消除进行中 */
  batchEraserLoading: false,
  singleReuploadSignal: 0,
  batchReuploadSignal: 0,
  batchReuploadPending: false,
  // 单图mask列表
  singleMaskImages: { people: '', glasses: '', watermark: '' },
}));

export default useAIEraserStore;

/**
 * 设置单图模式原始图
 *
 * @export
 * @param {string} singleOriginalImage
 */
export function setSingleOriginalImage(singleOriginalImage: string) {
  useAIEraserStore.setState(() => ({ singleOriginalImage }));
}

/**
 * 设置单图模式结果图
 *
 * @export
 * @param {string} singleResultImage
 */
export function setSingleResultImage(singleResultImage: string) {
  useAIEraserStore.setState(() => ({
    singleResultImage,
    ...(!singleResultImage ? { singleResultTaskId: '' } : {}),
  }));
}

/**
 * 设置单图模式结果对应的服务端任务 id。
 */
export function setSingleResultTaskId(singleResultTaskId: string) {
  useAIEraserStore.setState(() => ({ singleResultTaskId }));
}

export function setSingleMediaType(singleMediaType: 'image' | 'video') {
  useAIEraserStore.setState(() => ({ singleMediaType }));
}

/**
 * 设置当前模式
 * 切到单张时退出批量处理（避免顶部批量操作按钮仍展示）
 *
 * @export
 * @param {'single' | 'batch'} mode
 */
export function setMode(mode: 'single' | 'batch') {
  if (mode === 'single') {
    useAIEraserStore.setState((state) => ({
      mode: 'single',
      batchProcessingMode: false,
      previewImageIndex: null,
      batchImages: state.batchImages.map((item) => ({ ...item, selected: false })),
    }));
  } else {
    useAIEraserStore.setState(() => ({ mode: 'batch' }));
  }
}

/**
 * 设置单张-自动消除文字
 *
 * @export
 * @param {boolean} textSingleAutoEraser
 */
export function setTextSingleAutoEraser(textSingleAutoEraser: boolean) {
  useAIEraserStore.setState(() => ({ textSingleAutoEraser }));
}

/**
 * 设置单张-自动消除水印
 *
 * @export
 * @param {boolean} waterMarkSingleAutoEraser
 */
export function setWaterMarkSingleAutoEraser(waterMarkSingleAutoEraser: boolean) {
  useAIEraserStore.setState(() => ({ waterMarkSingleAutoEraser }));
}

/**
 * 设置单张-自动消除眼镜
 *
 * @export
 * @param {boolean} glassesSingleAutoEraser
 */
export function setGlassesSingleAutoEraser(glassesSingleAutoEraser: boolean) {
  useAIEraserStore.setState(() => ({ glassesSingleAutoEraser }));
}

/**
 * 设置单张-自动消除路人
 *
 * @export
 * @param {boolean} strangerSingleAutoEraser
 */
export function setStrangerSingleAutoEraser(strangerSingleAutoEraser: boolean) {
  useAIEraserStore.setState(() => ({ strangerSingleAutoEraser }));
}

/**
 * 设置单张-自动消除反光
 *
 * @export
 * @param {boolean} reflectionSingleAutoEraser
 */
export function setReflectionSingleAutoEraser(reflectionSingleAutoEraser: boolean) {
  useAIEraserStore.setState(() => ({ reflectionSingleAutoEraser }));
}

/**
 * 设置涂抹笔画粗细
 *
 * @export
 * @param {number} brushSize
 */
export function setBrushSize(brushSize: number) {
  useAIEraserStore.setState(() => ({ brushSize }));
}

/**
 * 设置单图模式消除loading
 *
 * @export
 * @param {boolean} singleEraserLoading
 */
export function setSingleEraserLoading(singleEraserLoading: boolean) {
  useAIEraserStore.setState(() => ({ singleEraserLoading }));
}

/**
 * 设置手动消除是否启用橡皮擦
 *
 * @export
 * @param {boolean} manualEnableEraser
 */
export function setManualEnableEraser(manualEnableEraser: boolean) {
  useAIEraserStore.setState(() => ({ manualEnableEraser }));
}

/**
 * 设置单图模式对比中
 *
 * @export
 * @param {boolean} singleCompareIng
 */
export function setSingleCompareIng(singleCompareIng: boolean) {
  useAIEraserStore.setState(() => ({ singleCompareIng }));
}

/**
 * 清空单图模式参数
 *
 * @export
 */
export function clearSingleParams() {
  useAIEraserStore.setState(() => ({
    textSingleAutoEraser: false,
    waterMarkSingleAutoEraser: false,
    glassesSingleAutoEraser: false,
    strangerSingleAutoEraser: false,
    reflectionSingleAutoEraser: false,
    singleMaskImages: { people: '', glasses: '', watermark: '' },
  }));
}

/**
 * 设置批量模式自动消除类型（与 AIEraserScene 对应，最多一项开启，传当前值则关闭）
 *
 * @export
 * @param {AIEraserScene} type
 */
export function setBatchAutoEraserType(type: AIEraserScene) {
  useAIEraserStore.setState((state) => ({
    batchAutoEraserType: state.batchAutoEraserType === type ? null : type,
  }));
}

/**
 * 获取当前批量自动消除类型（点击消除时调用，对应 AIEraserScene）
 *
 * @export
 */
export function getBatchAutoEraserType(): BatchAutoEraserType {
  return useAIEraserStore.getState().batchAutoEraserType;
}

/**
 * 更新单张批量图片的状态（处理中/成功/失败等）
 *
 * @export
 * @param {string} id
 * @param {Partial<Pick<BatchImageItem, 'status' | 'progress' | 'resultUrl' | 'resultImageLoading' | 'errorText' | 'taskId' | 'width' | 'height'>>} patch
 */
export function updateBatchImage(
  id: string,
  patch: Partial<Pick<BatchImageItem, 'status' | 'progress' | 'resultUrl' | 'resultImageLoading' | 'errorText' | 'taskId' | 'width' | 'height'>>,
) {
  useAIEraserStore.setState((state) => ({
    batchImages: state.batchImages.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  }));
}

/**
 * 设置批量消除 loading 状态
 *
 * @export
 * @param {boolean} loading
 */
export function setBatchEraserLoading(loading: boolean) {
  useAIEraserStore.setState(() => ({ batchEraserLoading: loading }));
}

/**
 * 设置批量图片列表
 *
 * @export
 * @param {BatchImageItem[]} batchImages
 */
export function setBatchImages(batchImages: BatchImageItem[]) {
  useAIEraserStore.setState(() => ({ batchImages }));
}

/**
 * 追加批量图片（上传后调用）
 *
 * @export
 * @param {BatchImageItem[]} newItems
 */
export function addBatchImages(newItems: BatchImageItem[]) {
  useAIEraserStore.setState((state) => ({
    batchImages: [...newItems, ...state.batchImages],
  }));
}

/**
 * 设置批量处理选择模式（进入时所有图片变为可选；退出时重置选中状态）
 *
 * @export
 * @param {boolean} active
 */
export function setBatchProcessingMode(active: boolean) {
  useAIEraserStore.setState((state) => {
    const batchImages = active
      ? state.batchImages
      : state.batchImages.map((item) => ({ ...item, selected: false }));
    return { batchProcessingMode: active, batchImages };
  });
}

/**
 * 切换单张批量图片的选中状态
 *
 * @export
 * @param {string} id
 */
export function toggleBatchImageSelected(id: string) {
  useAIEraserStore.setState((state) => {
    const batchImages = state.batchImages.map((item) => {
      if (item.id === id) return { ...item, selected: !item.selected };
      return item;
    });
    return { batchImages };
  });
}

/**
 * 从列表中移除一张批量图片
 * 若当前正在预览被删项或在其之前的项，会同步更新或关闭预览索引
 *
 * @export
 * @param {string} id
 */
export function removeBatchImage(id: string) {
  useAIEraserStore.setState((state) => {
    const { batchImages: stateBatchImages, previewImageIndex } = state;
    const removedIndex = stateBatchImages.findIndex((img) => img.id === id);
    const batchImages = stateBatchImages.filter((img) => img.id !== id);

    let nextPreviewIndex = previewImageIndex;
    if (previewImageIndex !== null && removedIndex >= 0) {
      if (removedIndex === previewImageIndex) {
        nextPreviewIndex = null;
      } else if (removedIndex < previewImageIndex) {
        nextPreviewIndex = Math.max(0, previewImageIndex - 1);
      }
    }

    return { batchImages, previewImageIndex: nextPreviewIndex };
  });
}

/**
 * 设置大图预览索引（null 退出预览）
 *
 * @export
 * @param {number | null} index
 */
export function setPreviewImageIndex(index: number | null) {
  useAIEraserStore.setState(() => ({ previewImageIndex: index }));
}

/**
 * 取消全部批量图片的选中状态并退出选择模式
 *
 * @export
 */
export function cancelBatchSelection() {
  useAIEraserStore.setState((state) => ({
    batchProcessingMode: false,
    batchImages: state.batchImages.map((item) => ({ ...item, selected: false })),
  }));
}

/**
 * 触发批量重新上传（仅打开选择框，不清空列表；确认上传后由 BatchList onSuccess 根据 batchReuploadPending 替换）
 *
 * @export
 */
export function triggerBatchReupload() {
  useAIEraserStore.setState((state) => ({
    batchReuploadSignal: state.batchReuploadSignal + 1,
  }));
}

export function triggerSingleReupload() {
  useAIEraserStore.setState((state) => ({
    singleReuploadSignal: state.singleReuploadSignal + 1,
  }));
}

/**
 * 设置「下次上传是否替换列表」（点击重新上传时设为 true，选择图片确认上传后置回 false）
 *
 * @export
 */
export function setBatchReuploadPending(pending: boolean) {
  useAIEraserStore.setState(() => ({ batchReuploadPending: pending }));
}

/**
 * 设置单图mask列表
 *
 * @export
 * @param {Partial<Record<'people' | 'glasses' | 'watermark', string>>} maskImages
 */
export function setSingleMaskImages(maskImages: Partial<Record<'people' | 'glasses' | 'watermark', string>>) {
  useAIEraserStore.setState((state) => ({ singleMaskImages: { ...state.singleMaskImages, ...maskImages } }));
}

/**
 * 获取单图mask列表
 *
 * @export
 */
export function getSingleMaskImages(): Record<'people' | 'glasses' | 'watermark', string> {
  return useAIEraserStore.getState().singleMaskImages;
}

/**
 * 重置 AI Eraser 页面 store，避免再次进入时沿用旧数据
 *
 * @export
 */
export function resetAIEraserStore() {
  useAIEraserStore.setState(() => ({
    singleOriginalImage: '',
    singleResultImage: '',
    singleResultTaskId: '',
    singleCompareIng: false,
    singleMediaType: 'image',
    mode: 'single',
    manualEnableEraser: false,
    brushSize: 20,
    singleEraserLoading: false,
    textSingleAutoEraser: false,
    waterMarkSingleAutoEraser: false,
    glassesSingleAutoEraser: false,
    strangerSingleAutoEraser: false,
    reflectionSingleAutoEraser: false,
    batchImages: [],
    batchProcessingMode: false,
    previewImageIndex: null,
    batchAutoEraserType: null,
    batchEraserLoading: false,
    singleReuploadSignal: 0,
    batchReuploadSignal: 0,
    batchReuploadPending: false,
    singleMaskImages: { people: '', glasses: '', watermark: '' },
  }));
}
